import { type ActionButtonConfig, BDDialog } from '@brightdrop/bd-ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import ShareActionsSkeleton from './ShareActionsSkeleton';
import useStyles from './ShareViaEmailDialog.styles';
import ShareViaEmailForm from './ShareViaEmailForm';
import type {
  ShareRecipient,
  ShareViaEmailDialogProps,
  ShareViaEmailEntity,
} from './types';
import { isValidEmail } from './utils';

/**
 * Modal for sending an assistant response on by email.
 *
 * Owns the recipients list because `BDDialog` builds its footer from a
 * `buttonsConfig` the parent supplies, and Send's disabled state is
 * read straight off that list.
 *
 * Send stays enabled while a malformed address is in the field rather
 * than greying out, so pressing it can say what is wrong; an inert
 * button would leave the red chip unexplained.
 *
 * While `onSubmit` is pending the footer buttons give way to
 * skeletons and the dialog refuses every dismissal, so the user
 * cannot walk away from a send that is still deciding its outcome.
 * It closes once the promise settles; the caller reports the result.
 *
 * @param open - Whether the dialog is mounted open.
 * @param onClose - Called on Cancel, on close, and after a send settles.
 * @param onSubmit - Sends to the chosen recipients when Send is pressed.
 */
function ShareViaEmailDialog({
  open,
  onClose,
  onSubmit,
}: ShareViaEmailDialogProps) {
  const { classes } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const [recipients, setRecipients] = useState<ShareRecipient[]>([]);
  const [hasMalformedRecipient, setHasMalformedRecipient] = useState(false);
  const [isSending, setIsSending] = useState(false);
  // State only reaches the next render; the ref refuses a second Send
  // that lands before it.
  const isSendingRef = useRef(false);

  // The disabled fieldset locks native controls but not a focused
  // chip's Backspace/Delete, so the list itself refuses edits mid-send
  // to keep it matching what was sent.
  const handleRecipientsChange = useCallback((next: ShareRecipient[]) => {
    if (isSendingRef.current) {
      return;
    }
    setRecipients(next);
    setHasMalformedRecipient(false);
  }, []);

  const closeDialog = useCallback(() => {
    setRecipients([]);
    setHasMalformedRecipient(false);
    onClose();
  }, [onClose]);

  // Escape and the backdrop reach here through `BDDialog`, so this is
  // the one place a dismissal mid-send can be refused.
  const handleClose = useCallback(() => {
    if (isSending) {
      return;
    }
    closeDialog();
  }, [closeDialog, isSending]);

  const handleSend = useCallback(async () => {
    if (isSendingRef.current) {
      return;
    }
    if (!recipients.every(({ email }) => isValidEmail(email))) {
      setHasMalformedRecipient(true);
      return;
    }

    isSendingRef.current = true;
    setIsSending(true);
    // Reporting the outcome is the caller's job; the dialog closes
    // either way.
    await onSubmit?.(recipients).catch(() => undefined);
    isSendingRef.current = false;
    setIsSending(false);
    closeDialog();
  }, [closeDialog, onSubmit, recipients]);

  // The message is translated here rather than held in state so a
  // language change while the dialog is open reaches it.
  const entity = useMemo<ShareViaEmailEntity>(
    () => ({
      recipients,
      onRecipientsChange: handleRecipientsChange,
      errorMessage: hasMalformedRecipient
        ? t('chat.share.dialog.malformedEmailError')
        : undefined,
      isSending,
    }),
    [handleRecipientsChange, hasMalformedRecipient, isSending, recipients, t]
  );

  const buttonsConfig = useMemo<ActionButtonConfig[]>(
    () =>
      isSending
        ? []
        : [
            {
              id: 'ai-sidekick-share-cancel',
              variant: 'secondary',
              displayLabel: t('chat.share.dialog.cancel'),
              accessibleLabel: t('chat.share.dialog.cancel'),
              onClick: handleClose,
            },
            {
              id: 'ai-sidekick-share-send',
              displayLabel: t('chat.share.dialog.send'),
              accessibleLabel: t('chat.share.dialog.send'),
              disabled: recipients.length === 0,
              onClick: handleSend,
            },
          ],
    [handleClose, handleSend, isSending, recipients.length, t]
  );

  return (
    <BDDialog<ShareViaEmailEntity>
      openModal={open}
      onCloseModal={handleClose}
      component={ShareViaEmailForm}
      entity={entity}
      title={t('chat.share.dialog.title')}
      dialogSize="sm"
      className={classes.dialog}
      dataTestId="ai-sidekick-share-dialog"
      buttonsConfig={buttonsConfig}
      customActionsContent={isSending ? <ShareActionsSkeleton /> : undefined}
      disableClose={isSending}
      disableBackdropClick={isSending}
      // `fleet-management` wraps `BDDialog` to supply these, but that
      // wrapper is app-level and out of reach here, so the package
      // translates its own.
      labels={{
        aria: t('chat.share.dialog.ariaLabel'),
        close: t('chat.share.dialog.closeAriaLabel'),
      }}
    />
  );
}

export default ShareViaEmailDialog;
